import React, { useState } from 'react';
import { View, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Text from '../common/Text';
import Button from '../common/Button';
import { PRICING_TIERS, PricingTier } from '../../data/landingData';
import { trackButtonClick } from '../../utils/analytics';

type ThemeColors = {
  text: string;
  secondary: string;
  card: string;
  border: string;
  success: string;
};

type Props = {
  colors: ThemeColors;
  isMobile: boolean;
  onGetStarted: () => void;
};

const PricingCard: React.FC<{
  tier: PricingTier;
  isAnnual: boolean;
  isMobile: boolean;
  colors: ThemeColors;
  onPress: () => void;
}> = ({ tier, isAnnual, isMobile, colors, onPress }) => {
  const price =
    isAnnual && tier.annualPrice > 0
      ? (tier.annualPrice / 12).toFixed(2)
      : tier.monthlyPrice.toFixed(2);
  const isFree = tier.monthlyPrice === 0;
  const isPopular = tier.badge === 'Most Popular';

  return (
    <View
      style={[
        styles.cardWrapper,
        isMobile && styles.cardWrapperMobile,
        isPopular && !isMobile && styles.cardWrapperPopular,
      ]}
    >
      {tier.badge && (
        <View
          style={[
            styles.badge,
            { backgroundColor: isPopular ? tier.color : tier.color + '22' },
          ]}
        >
          <Text
            style={[
              styles.badgeText,
              { color: isPopular ? '#fff' : tier.color },
            ]}
          >
            {tier.badge}
          </Text>
        </View>
      )}

      <View
        style={[
          styles.card,
          isMobile && styles.cardMobile,
          isPopular && styles.cardPopular,
          {
            backgroundColor: isPopular ? tier.color + '0C' : colors.card,
            borderColor: isPopular ? tier.color : colors.border,
            ...(Platform.OS === 'web'
              ? {
                  boxShadow: isPopular
                    ? `0 8px 32px 0 ${tier.color}30`
                    : `0 2px 12px 0 ${colors.text}0F`,
                }
              : {
                  shadowColor: tier.color,
                  shadowOpacity: isPopular ? 0.2 : 0.06,
                  shadowRadius: isPopular ? 20 : 8,
                  shadowOffset: { width: 0, height: 4 },
                  elevation: isPopular ? 8 : 3,
                }),
          },
        ]}
      >
        {/* Accent top bar */}
        <View style={[styles.accentBar, { backgroundColor: tier.color }]} />

        <View
          style={[styles.cardContent, isMobile && styles.cardContentMobile]}
        >
          {/* Tier name */}
          <Text style={[styles.tierName, { color: colors.text }]}>
            {tier.name}
          </Text>

          {/* Price */}
          <View style={styles.priceRow}>
            {isFree ? (
              <Text style={[styles.priceFree, { color: tier.color }]}>
                Free
              </Text>
            ) : (
              <>
                <Text
                  style={[styles.priceCurrency, { color: colors.secondary }]}
                >
                  $
                </Text>
                <Text style={[styles.priceValue, { color: tier.color }]}>
                  {price}
                </Text>
                <Text style={[styles.pricePeriod, { color: colors.secondary }]}>
                  /mo
                </Text>
              </>
            )}
          </View>

          {isAnnual && !isFree && (
            <Text style={[styles.annualNote, { color: colors.secondary }]}>
              Billed ${tier.annualPrice}/year — save{' '}
              {Math.round(
                100 - (tier.annualPrice / (tier.monthlyPrice * 12)) * 100
              )}
              %
            </Text>
          )}
          {!isAnnual && !isFree && (
            <Text style={[styles.annualNote, { color: colors.secondary }]}>
              Billed monthly
            </Text>
          )}
          {isFree && (
            <Text style={[styles.annualNote, { color: colors.secondary }]}>
              No credit card required
            </Text>
          )}

          {/* Description */}
          <Text style={[styles.description, { color: colors.secondary }]}>
            {tier.description}
          </Text>

          {/* Feature list */}
          <View style={styles.features}>
            {tier.features.map((f) => (
              <View key={f.text} style={styles.featureRow}>
                <Ionicons
                  name={
                    f.included ? 'checkmark-circle' : 'close-circle-outline'
                  }
                  size={18}
                  color={f.included ? tier.color : colors.secondary}
                  style={{ opacity: f.included ? 1 : 0.4 }}
                />
                <Text
                  style={[
                    styles.featureText,
                    { color: f.included ? colors.text : colors.secondary },
                    !f.included && styles.featureTextExcluded,
                  ]}
                >
                  {f.text}
                </Text>
              </View>
            ))}
          </View>

          {/* CTA */}
          <Button
            title={tier.cta}
            onPress={onPress}
            variant={tier.ctaVariant}
            style={[
              styles.cta,
              tier.ctaVariant === 'primary'
                ? { backgroundColor: tier.color, borderColor: tier.color }
                : { borderColor: tier.color },
            ]}
            textStyle={[
              styles.ctaText,
              tier.ctaVariant === 'primary'
                ? { color: '#fff' }
                : { color: tier.color },
            ]}
            trackClick={true}
            trackingLocation="landing-page-pricing-section"
            trackingData={{
              button_name: tier.cta,
              plan: tier.id,
              button_type: 'pricing_cta',
              price: tier.monthlyPrice,
            }}
          />
        </View>
      </View>
    </View>
  );
};

const PricingSection: React.FC<Props> = ({
  colors,
  isMobile,
  onGetStarted,
}) => {
  const [isAnnual, setIsAnnual] = useState(false);

  return (
    <View style={[styles.section, isMobile && styles.sectionMobile]}>
      {/* Header */}
      <View style={[styles.header, isMobile && styles.headerMobile]}>
        <Text style={[styles.eyebrow, { color: colors.secondary }]}>
          Simple Pricing
        </Text>
        <Text
          style={[
            styles.title,
            isMobile && styles.titleMobile,
            { color: colors.text },
          ]}
        >
          Start Free. Upgrade When Ready.
        </Text>
        <Text
          style={[
            styles.subtitle,
            isMobile && styles.subtitleMobile,
            { color: colors.secondary },
          ]}
        >
          No surprise charges. Cancel any time. Beta pricing locked in for early
          adopters.
        </Text>
      </View>

      {/* Billing toggle */}
      <View style={[styles.toggleRow, isMobile && styles.toggleRowMobile]}>
        <Text
          style={[
            styles.toggleLabel,
            { color: isAnnual ? colors.secondary : colors.text },
          ]}
        >
          Monthly
        </Text>
        <TouchableOpacity
          style={[
            styles.toggleTrack,
            { backgroundColor: isAnnual ? '#9475e1' : colors.border },
          ]}
          onPress={() => {
            setIsAnnual((v) => !v);
            trackButtonClick(
              isAnnual ? 'Billing Toggle Monthly' : 'Billing Toggle Annual',
              'landing-page-pricing-section',
              {
                button_type: 'billing_toggle',
              }
            );
          }}
          activeOpacity={0.8}
          {...(isMobile
            ? { hitSlop: { top: 14, bottom: 14, left: 12, right: 12 } }
            : {})}
        >
          <View
            style={[
              styles.toggleThumb,
              { transform: [{ translateX: isAnnual ? 22 : 2 }] },
            ]}
          />
        </TouchableOpacity>
        <Text
          style={[
            styles.toggleLabel,
            { color: isAnnual ? colors.text : colors.secondary },
          ]}
        >
          Annual{' '}
          <Text style={[styles.savingsBadge, { color: '#9475e1' }]}>
            Save up to 35%
          </Text>
        </Text>
      </View>

      {/* Cards */}
      <View style={[styles.cards, isMobile && styles.cardsMobile]}>
        {PRICING_TIERS.map((tier) => (
          <PricingCard
            key={tier.id}
            tier={tier}
            isAnnual={isAnnual}
            isMobile={isMobile}
            colors={colors}
            onPress={onGetStarted}
          />
        ))}
      </View>

      {/* Footer note */}
      <Text
        style={[
          styles.footerNote,
          isMobile && styles.footerNoteMobile,
          { color: colors.secondary },
        ]}
      >
        All paid plans include a 7-day free trial. No credit card required for
        Free tier.
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    paddingHorizontal: 24,
    paddingVertical: 40,
  },
  sectionMobile: {
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 32,
    width: '100%',
    flexGrow: 0,
  },
  header: {
    alignItems: 'center',
    marginBottom: 22,
  },
  headerMobile: {
    marginBottom: 14,
  },
  eyebrow: {
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 2,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: '900',
    textAlign: 'center',
    letterSpacing: -0.8,
    lineHeight: 34,
    marginBottom: 8,
  },
  titleMobile: {
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: -0.4,
  },
  subtitle: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    opacity: 0.8,
    maxWidth: 520,
  },
  subtitleMobile: {
    fontSize: 14,
    lineHeight: 21,
    maxWidth: undefined,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 28,
    flexWrap: 'wrap',
  },
  toggleRowMobile: {
    marginBottom: 18,
    rowGap: 8,
  },
  toggleLabel: {
    fontSize: 15,
    fontWeight: '600',
  },
  toggleTrack: {
    width: 48,
    height: 26,
    borderRadius: 13,
    justifyContent: 'center',
  },
  toggleThumb: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 3,
  },
  savingsBadge: {
    fontWeight: '700',
    fontSize: 13,
  },
  cards: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    gap: 20,
    flexWrap: 'wrap',
  },
  cardsMobile: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    alignContent: 'flex-start',
    width: '100%',
    flexGrow: 0,
    gap: 16,
  },
  cardWrapper: {
    flex: 1,
    minWidth: 260,
    maxWidth: 320,
    position: 'relative',
  },
  cardWrapperMobile: {
    flex: 0,
    flexGrow: 0,
    flexShrink: 0,
    minWidth: 0,
    maxWidth: 9999,
    width: '100%',
    alignSelf: 'stretch',
    alignItems: 'stretch',
    marginBottom: 0,
  },
  cardWrapperPopular: {
    marginTop: -12,
  },
  badge: {
    alignSelf: 'center',
    paddingHorizontal: 14,
    paddingVertical: 5,
    borderRadius: 100,
    marginBottom: 8,
    zIndex: 1,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  card: {
    borderRadius: 20,
    borderWidth: 1.5,
    overflow: 'hidden',
  },
  cardMobile: {
    width: '100%',
    alignSelf: 'stretch',
    flexGrow: 0,
    flexShrink: 0,
    ...(Platform.OS === 'web'
      ? ({
          position: 'relative',
          overflow: 'hidden',
        } as object)
      : {}),
  },
  cardPopular: {
    borderWidth: 2,
  },
  accentBar: {
    height: 4,
    width: '100%',
  },
  cardContent: {
    padding: 24,
  },
  cardContentMobile: {
    padding: 16,
  },
  tierName: {
    fontSize: 20,
    fontWeight: '800',
    marginBottom: 12,
    letterSpacing: -0.3,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 4,
  },
  priceFree: {
    fontSize: 40,
    fontWeight: '900',
    letterSpacing: -1,
    lineHeight: 44,
  },
  priceCurrency: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 6,
    marginRight: 2,
  },
  priceValue: {
    fontSize: 48,
    fontWeight: '900',
    letterSpacing: -2,
    lineHeight: 52,
  },
  pricePeriod: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 8,
    marginLeft: 2,
  },
  annualNote: {
    fontSize: 12,
    marginBottom: 16,
    fontWeight: '500',
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    opacity: 0.8,
    marginBottom: 20,
  },
  features: {
    gap: 10,
    marginBottom: 24,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  featureText: {
    fontSize: 14,
    lineHeight: 20,
    flex: 1,
  },
  featureTextExcluded: {
    opacity: 0.4,
  },
  cta: {
    height: 48,
    borderRadius: 12,
    alignSelf: 'stretch',
    borderWidth: 2,
  },
  ctaText: {
    fontSize: 15,
    fontWeight: '700',
  },
  footerNote: {
    textAlign: 'center',
    fontSize: 13,
    marginTop: 28,
    opacity: 0.7,
  },
  footerNoteMobile: {
    marginTop: 22,
    marginBottom: 4,
  },
});

export default PricingSection;
